import React, {
  CSSProperties,
  FC,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { APP_TOKENS as tokens } from "../../../../styles/tokens";
import {
  PhotoGuidanceExample,
  PhotoUploadGuidance,
} from "../../../pages/home/simulate/photoUploadGuidance";
import "./UploadImage.css";
import UploadImageButton from "./UploadImageButton";

interface UploadImageProps {
  guidance: PhotoUploadGuidance;
  saveCurrentImage: (image: Blob) => void;
}

interface PhotoExampleProps {
  example: PhotoGuidanceExample;
}

type UploadCssProperties = CSSProperties &
  Record<`--upload-image-${string}`, string | number>;

const PhotoExample: FC<PhotoExampleProps> = ({ example }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const isGood = example.kind === "good";

  useEffect(() => setImageFailed(false), [example.imageUrl]);

  return (
    <div className="upload-image__example">
      {imageFailed ? (
        <span className="upload-image__example-fallback">Unavailable</span>
      ) : (
        <img
          alt={example.alt}
          className={`upload-image__example-image upload-image__example-image--${example.kind}`}
          draggable={false}
          onError={() => setImageFailed(true)}
          src={example.imageUrl}
          style={
            example.zoom
              ? ({
                  "--upload-image-example-zoom": example.zoom,
                } as UploadCssProperties)
              : undefined
          }
        />
      )}
      <span
        aria-hidden="true"
        className={`upload-image__status upload-image__status--${isGood ? "good" : "bad"}`}
      >
        <svg
          height={tokens.fontSize.sm}
          viewBox="0 0 24 24"
          width={tokens.fontSize.sm}
        >
          <path
            d={
              isGood
                ? "M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
                : "M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.41 4.29 19.71 2.88 18.3 9.17 12 2.88 5.7 4.29 4.29 10.59 10.59 16.89 4.3z"
            }
          />
        </svg>
      </span>
      <span className="upload-image__example-label">{example.label}</span>
    </div>
  );
};

const UploadImage: FC<UploadImageProps> = ({ guidance, saveCurrentImage }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const guidanceId = useId();
  const errorId = useId();
  const [errorMessage, setErrorMessage] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  const saveFile = (selectedFile?: File) => {
    if (!selectedFile || !selectedFile.type.startsWith("image/")) {
      setErrorMessage("Choose an image file to continue.");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    setErrorMessage("");
    saveCurrentImage(selectedFile);
  };

  const cssVariables: UploadCssProperties = {
    "--upload-image-accent": tokens.color.accent,
    "--upload-image-border-strong": `${tokens.borderWidth.strong}px`,
    "--upload-image-border-thin": `${tokens.borderWidth.thin}px`,
    "--upload-image-error": tokens.color.errorText,
    "--upload-image-font-bold": tokens.font.bold,
    "--upload-image-font-medium": tokens.font.medium,
    "--upload-image-font-sm": `${tokens.fontSize.sm}px`,
    "--upload-image-font-xs": `${tokens.fontSize.xs}px`,
    "--upload-image-focus": tokens.color.accentDark,
    "--upload-image-inverse": tokens.color.inverse,
    "--upload-image-line-sm": `${tokens.lineHeight.sm}px`,
    "--upload-image-line-xs": `${tokens.lineHeight.xs}px`,
    "--upload-image-media-opacity": tokens.opacity.mutedMedia,
    "--upload-image-overlay-black": tokens.color.overlayBlack,
    "--upload-image-radius-md": `${tokens.radius.md}px`,
    "--upload-image-radius-round": `${tokens.radius.round}px`,
    "--upload-image-space-xs": `${tokens.spacing.xs}px`,
    "--upload-image-status-size": `${tokens.fontSize.lg}px`,
    "--upload-image-strip-height": `${tokens.spacing.massive * 2 + tokens.fontSize.sm}px`,
    "--upload-image-success": tokens.color.successText,
    "--upload-image-text-muted": tokens.color.textMuted,
  };

  return (
    <div
      aria-describedby={`${guidanceId}${errorMessage ? ` ${errorId}` : ""}`}
      aria-label="Upload a photo"
      data-dragging={isDragging || undefined}
      data-error={Boolean(errorMessage) || undefined}
      data-testid="simulation-photo-upload-guide"
      id="upload-image__outer"
      onDragEnter={(event) => {
        event.preventDefault();
        dragDepthRef.current += 1;
        setIsDragging(true);
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) setIsDragging(false);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
      }}
      onDrop={(event) => {
        event.preventDefault();
        dragDepthRef.current = 0;
        setIsDragging(false);
        saveFile(event.dataTransfer.files?.[0]);
      }}
      role="group"
      style={cssVariables}
    >
      <div className="upload-image__guidance-strip">
        {guidance.examples.map((example) => (
          <PhotoExample
            example={example}
            key={`${example.kind}-${example.imageUrl}`}
          />
        ))}
      </div>
      <p className="upload-image__hint" id={guidanceId}>
        {guidance.title}
      </p>
      {errorMessage ? (
        <p className="upload-image__error" id={errorId} role="alert">
          {errorMessage}
        </p>
      ) : null}
      <UploadImageButton
        describedBy={`${guidanceId}${errorMessage ? ` ${errorId}` : ""}`}
        hasError={Boolean(errorMessage)}
        inputRef={inputRef}
        onFileSelected={saveFile}
      />
    </div>
  );
};

export default UploadImage;
